import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Shell } from "../components/Shell";
import { LANGS } from "../lib/i18n";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Trash2, Plus } from "lucide-react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Profile() {
    const { t, user, setUser, language, setLanguage, refreshUser, logout } = useApp();
    const nav = useNavigate();
    const [memories, setMemories] = useState([]);
    const [reflections, setReflections] = useState([]);
    const [memOn, setMemOn] = useState(user?.consents?.memory || false);
    const [newRefl, setNewRefl] = useState("");

    const load = useCallback(async () => {
        try {
            const [m, r] = await Promise.all([api.get("/memories"), api.get("/reflections")]);
            setMemories(m.data.memories); setMemOn(m.data.memory_enabled);
            setReflections(r.data.reflections);
        } catch (e) {}
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleMemory = async (on) => {
        setMemOn(on);
        try { const { data } = await api.patch("/auth/me", { memory_consent: on }); setUser(data); toast.success(t("common.saved")); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); setMemOn(!on); }
    };

    const delMemory = async (id) => { await api.delete(`/memories/${id}`); setMemories((m) => m.filter((x) => x.mem_id !== id)); };
    const clearMemories = async () => { await api.delete("/memories"); setMemories([]); toast.success("Cleared"); };

    const addRefl = async () => {
        if (newRefl.trim().length < 2) return;
        const { data } = await api.post("/reflections", { content: newRefl.trim() });
        setReflections((r) => [data.reflection, ...r]); setNewRefl(""); toast.success(t("common.saved"));
    };
    const delRefl = async (id) => { await api.delete(`/reflections/${id}`); setReflections((r) => r.filter((x) => x.refl_id !== id)); };

    const deleteAccount = async () => {
        try { await api.post("/privacy/delete-account"); toast.success("Account deleted."); await logout(); nav("/"); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    };

    return (
        <Shell eyebrow={user?.name ? `Hi, ${user.name}` : "Account"} title={t("profile.title")}>
            <div className="space-y-6">
                {/* Language */}
                <div className="card-soft p-6">
                    <h3 className="text-xl text-forest">{t("profile.language")}</h3>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {LANGS.map((l) => (
                            <button key={l.code} data-testid={`profile-lang-${l.code}`} onClick={() => { setLanguage(l.code); toast.success(t("common.saved")); }}
                                className={`px-4 py-2 rounded-full border text-sm ${language === l.code ? "border-forest bg-forest/5 font-semibold" : "border-charcoal/15 hover:bg-charcoal/5"}`}>{l.label}</button>
                        ))}
                    </div>
                </div>

                {/* Memory */}
                <div className="card-soft p-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl text-forest">{t("profile.memory")}</h3>
                        <Switch data-testid="memory-toggle" checked={memOn} onCheckedChange={toggleMemory} />
                    </div>
                    <p className="mt-2 text-sm text-charcoal/70">{memOn ? t("profile.memory_on") : t("profile.memory_off")}</p>
                    {memOn && (
                        <div className="mt-5">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium">{t("profile.view_memories")}</p>
                                {memories.length > 0 && <button data-testid="clear-memories" onClick={clearMemories} className="text-xs text-destructive hover:underline">{t("profile.clear_memories")}</button>}
                            </div>
                            {memories.length === 0 ? <p className="text-sm text-charcoal/50">{t("profile.no_memories")}</p> : (
                                <ul className="space-y-2">
                                    {memories.map((m) => (
                                        <li key={m.mem_id} className="flex items-start justify-between gap-3 text-sm bg-forest/5 rounded-xl px-3 py-2">
                                            <span>{m.content}</span>
                                            <button data-testid="delete-memory" onClick={() => delMemory(m.mem_id)} className="text-charcoal/50 hover:text-destructive shrink-0"><Trash2 size={15} /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>

                {/* Reflections */}
                <div className="card-soft p-6">
                    <h3 className="text-xl text-forest">{t("profile.reflections")}</h3>
                    <div className="mt-4 flex gap-2">
                        <input data-testid="reflection-input" value={newRefl} onChange={(e) => setNewRefl(e.target.value)} placeholder="Write a note or goal to revisit…"
                            className="flex-1 rounded-xl border border-charcoal/15 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 text-sm" />
                        <button data-testid="add-reflection" onClick={addRefl} className="btn-primary px-4 py-2.5"><Plus size={18} /></button>
                    </div>
                    <ul className="mt-4 space-y-2">
                        {reflections.map((r) => (
                            <li key={r.refl_id} className="flex items-start justify-between gap-3 text-sm bg-ivory border border-charcoal/10 rounded-xl px-3 py-2">
                                <span>{r.content}</span>
                                <button onClick={() => delRefl(r.refl_id)} className="text-charcoal/50 hover:text-destructive shrink-0"><Trash2 size={15} /></button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Delete */}
                <div className="card-soft p-6 border-destructive/20">
                    <h3 className="text-xl text-destructive">{t("profile.delete")}</h3>
                    <p className="mt-2 text-sm text-charcoal/70">{t("profile.delete_confirm")}</p>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button data-testid="delete-account-button" className="mt-4 px-5 py-2.5 rounded-full border border-destructive text-destructive text-sm hover:bg-destructive/5">{t("profile.delete")}</button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white">
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t("profile.delete")}</AlertDialogTitle>
                                <AlertDialogDescription>{t("profile.delete_confirm")}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                <AlertDialogAction data-testid="confirm-delete-account" onClick={deleteAccount} className="bg-destructive text-white">{t("common.confirm")}</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </Shell>
    );
}
